import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle2, ExternalLink, ArrowRight, Shield } from 'lucide-react';

const BASESCAN = 'https://basescan.org/address';

// Sourced from src/data/honestStatus.js — single source of truth.
const KOJIRU_LISTING = [
  { name: 'KOJI (ERC-20)',       address: '0xb50007B292256E2e54253D2110013465f9441482', role: 'Protocol token' },
  { name: 'KojiruStakingV2',     address: '0xaae9ae4d52fe13384522e0ef4114b0a5f5d62d68', role: 'Staking + 2M KOJI reward pool' },
  { name: 'WithdrawalGuard',     address: '0xE3D4B3BA88f53C2D2a499cb46Cd4679dE6c2021E', role: '10%/24h withdrawal cap' },
  { name: 'GuardianPause',       address: '0x74642654bFf0AB1F22d8747396cb1eC217f04Db3', role: 'Emergency circuit breaker' },
  { name: 'KojiruACSHook',       address: '0x05F2541827104393257FEc27C61b8Ea5373e0874', role: 'Slash + route in same tx (USDC-bound)' },
  { name: 'KojiruACSOracle',     address: '0x2364493206Aa7435050780bf710F934189B51ed6', role: 'EIP-712 attestation bridge (paired with Hook)' },
  { name: 'HarvestVault',        address: '0xB6a79B470D480A436aC59837Ec07F36FafE0c4C5', role: 'Revenue distribution rail' },
];

export default function X402Audit() {
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="x402-audit-page">
      <Helmet>
        <title>x402 Directory Audit | Kojiru — The Directories Disagree</title>
        <meta name="description" content="An August 2026 audit found 12% of x402 services listed under conflicting chains. Kojiru publishes its own canonical mapping so a skeptical merchant can verify every claim on BaseScan." />
      </Helmet>
      <Navbar />

      {/* HERO */}
      <section className="pt-32 pb-16 px-6" data-testid="x402-hero">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs uppercase tracking-widest text-red-300 mb-6" data-testid="x402-hero-badge">
              <AlertTriangle className="w-3.5 h-3.5" />
              Companion asset to "The Directories Disagree"
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-6" data-testid="x402-hero-headline">
              The directories disagree.{' '}
              <br className="hidden sm:block" />
              <span className="text-red-300">The chain doesn't.</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-3xl leading-relaxed">
              An August 2026 audit indexed <strong className="text-white">272 x402 services</strong> across two public directories. Zero of the 272 had identical chain metadata. For roughly <strong className="text-white">12% (33 hosts)</strong>, the two directories named different chains outright — Base vs. Arbitrum, Solana vs. Base, Sepolia vs. Amoy. That's the field an agent reads to route USDC.
            </p>
          </motion.div>
        </div>
      </section>

      {/* STAT STRIP */}
      <section className="py-8 px-6 border-y border-white/5 bg-white/[0.02]">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="x402-stat-strip">
            {[
              { n: '272', label: 'x402 services indexed' },
              { n: '0',   label: 'agree across directories' },
              { n: '33',  label: 'name different chains outright' },
              { n: '12%', label: 'of the sample — real conflicts' },
            ].map((s) => (
              <div key={s.label} className="text-center" data-testid={`x402-stat-${s.n}`}>
                <div className="text-4xl sm:text-5xl font-bold text-red-300 leading-none mb-2">{s.n}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-semibold">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIDE-BY-SIDE */}
      <section className="py-16 px-6" data-testid="x402-comparison">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-10">
            <div className="text-[11px] font-mono tracking-[0.22em] text-red-400 mb-3">// COHERENT VS AMBIGUOUS</div>
            <h2 className="text-2xl sm:text-3xl font-bold">
              This is what a coherent listing looks like.
            </h2>
            <p className="text-gray-400 mt-3 max-w-2xl">
              One chain (Base, <code className="text-emerald-400 text-sm">eip155:8453</code>), one contract per primitive, one canonical address, one BaseScan link. Verifiable in 60 seconds by any skeptical merchant, PSP, or auditor.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* KOJIRU */}
            <div className="rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/[0.04] to-emerald-500/[0.01] p-6" data-testid="x402-kojiru-listing">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-semibold mb-4">
                <CheckCircle2 className="w-3 h-3" />
                Kojiru — unambiguous canonical mapping
              </div>
              <div className="text-xs text-gray-400 mb-4">
                Chain: <code className="text-emerald-400">eip155:8453</code> (Base mainnet) · Settlement: USDC · Verification: <code className="text-emerald-400">block.chainid</code>
              </div>
              <div className="space-y-2.5" data-testid="x402-kojiru-contracts">
                {KOJIRU_LISTING.map((c) => (
                  <a
                    key={c.address}
                    href={`${BASESCAN}/${c.address}`}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="block rounded-lg border border-white/10 bg-black/40 p-3 hover:border-emerald-500/40 transition-colors"
                    data-testid={`x402-contract-${c.name.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-white">{c.name}</span>
                      <ExternalLink className="w-3 h-3 text-gray-500 ml-auto" />
                    </div>
                    <div className="text-[11px] font-mono text-gray-400 break-all">{c.address}</div>
                    <div className="text-[11px] text-gray-500 mt-1">{c.role}</div>
                  </a>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-emerald-500/20 text-xs text-gray-400 leading-relaxed">
                Also published as a machine-readable JSON manifest at{' '}
                <a href="https://kojiru.com/.well-known/x402-recourse.json" target="_blank" rel="noreferrer noopener" className="text-emerald-300 underline">kojiru.com/.well-known/x402-recourse.json</a>{' '}
                — any agent, wallet, or third-party directory can fetch the mapping deterministically.
              </div>
            </div>

            {/* WHAT DIRECTORIES SHOW */}
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="x402-directory-ambiguity">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-gray-500 font-semibold mb-4">
                <AlertTriangle className="w-3 h-3" />
                What the audit found across the sample
              </div>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="rounded-lg border border-white/10 bg-black/30 p-3">
                  <div className="text-xs text-gray-500 mb-1">SERVICE X, DIRECTORY A</div>
                  <div className="font-mono text-white">chain: <span className="text-emerald-300">eip155:8453</span> <span className="text-gray-500">(Base)</span></div>
                  <div className="text-xs text-gray-500 mt-2 mb-1">SAME SERVICE, DIRECTORY B</div>
                  <div className="font-mono text-white">chain: <span className="text-red-300">eip155:42161</span> <span className="text-gray-500">(Arbitrum)</span></div>
                </div>
                <div className="rounded-lg border border-white/10 bg-black/30 p-3">
                  <div className="text-xs text-gray-500 mb-1">SERVICE Y, DIRECTORY A</div>
                  <div className="font-mono text-white">chain: <span className="text-emerald-300">SOL</span> <span className="text-gray-500">(Solana)</span></div>
                  <div className="text-xs text-gray-500 mt-2 mb-1">SAME SERVICE, DIRECTORY B</div>
                  <div className="font-mono text-white">chain: <span className="text-red-300">BSE</span> <span className="text-gray-500">(generic Base short code)</span></div>
                </div>
                <div className="rounded-lg border border-white/10 bg-black/30 p-3">
                  <div className="text-xs text-gray-500 mb-1">SERVICE Z, DIRECTORY A</div>
                  <div className="font-mono text-white">chain: <span className="text-emerald-300">base-sepolia</span></div>
                  <div className="text-xs text-gray-500 mt-2 mb-1">SAME SERVICE, DIRECTORY B</div>
                  <div className="font-mono text-white">chain: <span className="text-red-300">polygon-amoy</span></div>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-white/10 text-xs text-gray-400 leading-relaxed">
                Each directory looks internally confident. The disagreement is only visible when you compare them. Twelve percent of the time, the merchant on the other end is on a different chain than the agent thinks it is.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE RULE */}
      <section className="py-16 px-6 border-t border-white/5 bg-white/[0.02]" data-testid="x402-rule-section">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="text-[11px] font-mono tracking-[0.22em] text-emerald-400 mb-4">// THE ARCHITECTURAL CHOICE</div>
          <blockquote className="text-2xl sm:text-3xl font-semibold leading-tight text-white max-w-3xl mx-auto mb-4">
            Kojiru trusts the chain over the directory.
            <br className="hidden sm:block" />
            <span className="text-red-300">Every other rail trusts the directory over the chain.</span>
          </blockquote>
          <p className="text-base text-gray-400 max-w-2xl mx-auto mt-4">
            <code className="text-emerald-400 text-sm">KojiruACSHook.slash()</code> reads <code className="text-emerald-400 text-sm">block.chainid</code> from the contract's own execution context and reverts if the chain doesn't match the one the deployment was scoped to. The chain is not a lookup; it is a constraint the code cannot escape.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6" data-testid="x402-cta-section">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight">
            Read every claim. Verify every address.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <a href="https://kojiru.com/.well-known/x402-recourse.json" target="_blank" rel="noreferrer noopener" data-testid="x402-cta-manifest">
              <Button size="lg" className="bg-red-500 hover:bg-red-600 text-white font-semibold gap-2">
                View Canonical Manifest
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
            <Link to="/adl-m" data-testid="x402-cta-adlm">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 gap-2">
                Full Liability Map
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/compliance" data-testid="x402-cta-compliance">
              <Button size="lg" variant="ghost" className="text-white hover:bg-white/5 gap-2">
                Compliance & Limitations
              </Button>
            </Link>
          </div>
          <p className="text-xs text-gray-500 mt-8 max-w-2xl mx-auto">
            The 272-service audit finding is from a public LinkedIn post dated Aug 10, 2026. The auditor is not named on this page to keep the focus on the pattern.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
